import { PlateRenderElementProps, useEditorRef } from '@udecode/plate';
import dynamic from 'next/dynamic';
import * as React from 'react';
import { Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { useAuth } from 'src/shared/hooks/use-auth';

type ElementProps<TElement = { element: { lang?: string; code?: string } }> =
  PlateRenderElementProps & TElement;

const CodeBlockElement = ({
  attributes,
  children,
  element,
  nodeProps,
}: ElementProps): JSX.Element => {
  const editor = useEditorRef();
  const path = ReactEditor.findPath(editor, element);
  const isPublicFile = useAuth((s) => s.isPublicFile);
  const onChangeLang = (lang: string) => {
    Transforms.setNodes(editor, { lang } as any, { at: path });
  };
  const onChangeCode = (code: string) => {
    Transforms.setNodes(editor, { code } as any, { at: path });
  };
  return (
    <div {...attributes} {...nodeProps} className="my-5" contentEditable={false}>
      <div contentEditable={false}>
        <AceEditor
          placeholder="Code here..."
          mode={element.lang || 'javascript'}
          theme="solarized_light"
          name="blah2"
          fontSize={14}
          onChange={onChangeCode}
          showPrintMargin={false}
          showGutter={false}
          minLines={3}
          maxLines={Infinity}
          width="100%"
          highlightActiveLine={true}
          readOnly={isPublicFile}
          value={element.code || ''}
          setOptions={{
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: true,
            tabSize: 2,
            showLineNumbers: false,
          }}
        />
        <div className="absolute right-0 top-0">
          <select
            name="mode"
            disabled={isPublicFile}
            onChange={(e) => onChangeLang(e.target.value)}
            value={element.lang ?? languages[0]}
          >
            {languages.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </div>
      </div>
      {children}
    </div>
  );
};

const AceEditor = dynamic(
  async () => {
    const reactAce = await import('react-ace');

    // prevent warning in console about misspelled props name.
    await import('ace-builds/src-min-noconflict/ext-language_tools');

    // import your theme/mode here. <AceEditor mode="javascript" theme="solarized_dark" />
    await import('ace-builds/src-min-noconflict/theme-solarized_dark');
    await import('ace-builds/src-min-noconflict/mode-javascript');
    await import('ace-builds/src-min-noconflict/mode-java');
    await import('ace-builds/src-min-noconflict/mode-python');
    await import('ace-builds/src-min-noconflict/mode-xml');
    await import('ace-builds/src-min-noconflict/mode-ruby');
    await import('ace-builds/src-min-noconflict/mode-sass');
    await import('ace-builds/src-min-noconflict/mode-markdown');
    await import('ace-builds/src-min-noconflict/mode-mysql');
    await import('ace-builds/src-min-noconflict/mode-json');
    await import('ace-builds/src-min-noconflict/mode-html');
    await import('ace-builds/src-min-noconflict/mode-handlebars');
    await import('ace-builds/src-min-noconflict/mode-golang');
    await import('ace-builds/src-min-noconflict/mode-csharp');
    await import('ace-builds/src-min-noconflict/mode-elixir');
    await import('ace-builds/src-min-noconflict/mode-typescript');
    await import('ace-builds/src-min-noconflict/mode-css');

    // as @Holgrabus commented you can paste these file into your /public folder.
    // You will have to set basePath and setModuleUrl accordingly.
    const ace = await import('ace-builds/src-min-noconflict/ace');
    ace.config.set('basePath', 'https://cdn.jsdelivr.net/npm/ace-builds@1.4.8/src-noconflict/');
    ace.config.setModuleUrl(
      'ace/mode/javascript_worker',
      'https://cdn.jsdelivr.net/npm/ace-builds@1.4.8/src-noconflict/worker-javascript.js'
    );

    return reactAce;
  },
  {
    ssr: false, // react-ace doesn't support server side rendering as it uses the window object.
  }
);

export default CodeBlockElement;

const languages = [
  'javascript',
  'java',
  'python',
  'xml',
  'ruby',
  'sass',
  'markdown',
  'mysql',
  'json',
  'html',
  'handlebars',
  'golang',
  'csharp',
  'elixir',
  'typescript',
  'css',
];
